import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGlobalStoreShallow } from 'src/store/globalStore';
import * as yup from 'yup';

import { AsyncSelectField, Button } from '@speeki/global-ui-components';
import { SessionStorageManager } from '@speeki/storage-manager';

import { TooltipWithQuestionMark } from '@components/molecules/TooltipWithQuestionMark/TooltipWithQuestionMark';

import { useFindCompanyByName } from '@utils/hooks/useFindCompanyByName';

import { APIService } from '@services/ApiService';
import { AuthService } from '@services/AuthService/AuthService';
import { getPath } from '@services/RouteService/paths';

import { subsidiarySiteUuidFromUrlVar } from '@boot/reactiveVariables';
import { yupResolver } from '@hookform/resolvers/yup';

import { EnterEntityFormWrapper } from './EnterEntityForm.styled';

export const EnterEntityForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        control,
        formState: { errors, isSubmitted, isSubmitting, isValid: isFormValid },
        handleSubmit,
    } = useForm<EnterEntityFormValues>({
        resolver: yupResolver(EnterEntityFormSchema),
    });

    const { setCompanyInfo } = useGlobalStoreShallow(['setCompanyInfo']);
    const [findCompanyByName, { loading }] = useFindCompanyByName();

    const { field } = useController({
        control,
        name: 'tenantName',
    });

    const onSubmit = handleSubmit(({ tenantName }) => {
        if (!tenantName) return;
        const { apiUrl, label, uuid, value } = tenantName;

        SessionStorageManager.removeValue('subsidiarySite');
        subsidiarySiteUuidFromUrlVar(undefined);
        new APIService().setGraphAPI = apiUrl;
        AuthService.setTenantUuid(uuid);

        setCompanyInfo({
            uuid,
            apiUrl,
            code: value,
            name: label,
        });
        navigate(getPath({ path: '/' }));
    });

    const loadTenants = async (inputValue: string) => {
        if (inputValue.length > 2) {
            const { data } = await findCompanyByName({
                variables: {
                    inputFilters: {
                        tenantCount: 10,
                        tenantName: inputValue,
                    },
                },
            });

            return (
                data?.tenant.map(({ apiUrl, code, name, uuid }) => ({
                    uuid,
                    apiUrl,
                    label: name,
                    value: code,
                })) || []
            );
        }

        return [];
    };

    return (
        <EnterEntityFormWrapper>
            <AsyncSelectField
                {...field}
                cacheOptions
                error={!!errors.tenantName}
                isClearIndicator
                isClearable
                label={
                    <>
                        {t('ENTITY_NAME')}
                        <TooltipWithQuestionMark
                            testId="Okhi3xj"
                            text={t('WELCOME_TOOLTIP_TEXT')}
                        />
                    </>
                }
                loadOptions={loadTenants}
                message={t(
                    errors.tenantName?.message ||
                        errors.tenantName?.value?.message ||
                        '',
                )}
                placeholder={t('ENTER_ENTITY')}
                testId="select_enter-entity-name"
            />
            <Button
                disabled={isSubmitting || (!isFormValid && isSubmitted)}
                fullWidth
                isLoading={loading || isSubmitting}
                onClick={onSubmit}
                testId="button_continue"
                variant={'primary'}
            >
                {t('CONTINUE')}
            </Button>
        </EnterEntityFormWrapper>
    );
};

const EnterEntityFormSchema = yup.object({
    tenantName: yup
        .object({
            uuid: yup.string().required(),
            apiUrl: yup.string().required(),
            label: yup.string().required(),
            value: yup.string().required('FIELD_ENTITY_NAME_NOT_EXISTS'),
        })
        .nullable()
        .required('FIELD_ENTITY_NAME_NOT_EXISTS'),
});

type EnterEntityFormValues = yup.InferType<typeof EnterEntityFormSchema>;
